import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useWorldStore } from "@/components/world/store";
import { AIRPORTS } from "@/data/sections";

const KEYMAP = {
  up: ["ArrowUp", "w", "W"],
  down: ["ArrowDown", "s", "S"],
  left: ["ArrowLeft", "a", "A"],
  right: ["ArrowRight", "d", "D"],
  boost: ["Shift"],
  action: ["e", "E"],
  drop: [" "],
};

function resolveKey(e) {
  if (e.key === " " || e.code === "Space") return " ";
  return e.key;
}

export default function HUD() {
  const altitude = useWorldStore((s) => s.altitude);
  const speed = useWorldStore((s) => s.speed);
  const heading = useWorldStore((s) => s.heading);
  const position = useWorldStore((s) => s.position);
  const nearestId = useWorldStore((s) => s.nearestId);
  const nearestDistance = useWorldStore((s) => s.nearestDistance);
  const openId = useWorldStore((s) => s.openId);
  const setInput = useWorldStore((s) => s.setInput);
  const openPanel = useWorldStore((s) => s.openPanel);
  const dropParcel = useWorldStore((s) => s.dropParcel);
  const firePlayerLaser = useWorldStore((s) => s.firePlayerLaser);
  const releaseBubbles = useWorldStore((s) => s.releaseBubbles);
  const spaceMode = useWorldStore((s) => s.spaceMode);
  const underwaterMode = useWorldStore((s) => s.underwaterMode);
  const escapeCharge = useWorldStore((s) => s.escapeCharge);
  const transitionPhase = useWorldStore((s) => s.transitionPhase);
  const birdHits = useWorldStore((s) => s.birdHits);
  const lastHitAt = useWorldStore((s) => s.lastHitAt);
  const resetBirdHits = useWorldStore((s) => s.resetBirdHits);
  const dazzle = useWorldStore((s) => s.dazzle);
  const [mobile, setMobile] = useState(false);
  const [hitFlash, setHitFlash] = useState(false);

  useEffect(() => {
    if (!lastHitAt) return;
    setHitFlash(true);
    const t = setTimeout(() => setHitFlash(false), 600);
    return () => clearTimeout(t);
  }, [lastHitAt]);

  useEffect(() => {
    const check = () => setMobile(window.matchMedia("(pointer: coarse)").matches || window.innerWidth < 900);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Keyboard input
  useEffect(() => {
    const patch = (patch) => setInput(patch);

    const down = (e) => {
      if (openId) return; // ignore driving keys when panel open
      const k = resolveKey(e);
      if (KEYMAP.up.includes(k)) patch({ up: true });
      if (KEYMAP.down.includes(k)) patch({ down: true });
      if (KEYMAP.left.includes(k)) patch({ left: true });
      if (KEYMAP.right.includes(k)) patch({ right: true });
      if (KEYMAP.boost.includes(k)) patch({ boost: true });
      if (KEYMAP.action.includes(k)) {
        const st = useWorldStore.getState();
        // earth-return is auto-triggered by proximity, not by E key
        if (nearestId && nearestId !== "earth-return") {
          openPanel(nearestId);
        }
      }
      if (KEYMAP.drop.includes(k)) {
        // Ignore key auto-repeat to prevent laser spam
        if (e.repeat) {
          e.preventDefault && e.preventDefault();
          return;
        }
        const now = performance.now();
        // Cooldown - min 250 ms between shots/drops
        if (window.__greinch_lastDropAt && now - window.__greinch_lastDropAt < 250) {
          e.preventDefault && e.preventDefault();
          return;
        }
        window.__greinch_lastDropAt = now;
        const st = useWorldStore.getState();
        if (!st.openId) {
          if (st.underwaterMode) {
            releaseBubbles(st.position);
          } else if (st.spaceMode) {
            firePlayerLaser(st.position, st.heading, 0);
          } else {
            dropParcel(st.position, st.heading);
          }
        }
        e.preventDefault && e.preventDefault();
      }
    };
    const up = (e) => {
      const k = resolveKey(e);
      if (KEYMAP.up.includes(k)) patch({ up: false });
      if (KEYMAP.down.includes(k)) patch({ down: false });
      if (KEYMAP.left.includes(k)) patch({ left: false });
      if (KEYMAP.right.includes(k)) patch({ right: false });
      if (KEYMAP.boost.includes(k)) patch({ boost: false });
    };

    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, [openId, nearestId, setInput, openPanel, dropParcel, firePlayerLaser, releaseBubbles]);

  // Compute waypoint to nearest airport if none in landing radius
  const waypoint = useMemo(() => {
    let bestId = null;
    let bestD = Infinity;
    for (const ap of AIRPORTS) {
      const dx = position.x - ap.position[0];
      const dz = position.z - ap.position[2];
      const d = Math.sqrt(dx * dx + dz * dz);
      if (d < bestD) {
        bestD = d;
        bestId = ap.id;
      }
    }
    const ap = AIRPORTS.find((a) => a.id === bestId);
    if (!ap) return null;
    const dx = ap.position[0] - position.x;
    const dz = ap.position[2] - position.z;
    const bearing = Math.atan2(dx, -dz); // radians, 0 = forward (north)
    const relative = bearing - heading;
    return { ap, distance: bestD, angle: relative };
  }, [position, heading]);

  const holdBtn = (name) => ({
    onPointerDown: (e) => {
      e.preventDefault();
      setInput({ [name]: true });
    },
    onPointerUp: () => setInput({ [name]: false }),
    onPointerLeave: () => setInput({ [name]: false }),
    onPointerCancel: () => setInput({ [name]: false }),
  });

  return (
    <div className="pointer-events-none fixed inset-0 z-30 font-mono text-[#F4EADE]" data-testid="world-hud">
      {/* Dazzle overlay (sun) */}
      {dazzle > 0 && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `radial-gradient(circle at 65% 35%, rgba(255,240,180,${0.85 * dazzle}) 0%, rgba(255,220,120,${0.6 * dazzle}) 25%, rgba(255,255,255,0) 70%)`,
            mixBlendMode: "screen",
          }}
        />
      )}
      {/* Bird hit red flash */}
      {hitFlash && (
        <div
          className="absolute inset-0 pointer-events-none animate-pulse"
          style={{
            boxShadow: "inset 0 0 200px 80px rgba(233,75,75,0.6)",
            background: "radial-gradient(circle at center, transparent 40%, rgba(233,75,75,0.15) 100%)",
          }}
        />
      )}
      {/* Top brand + skip */}
      <div className="absolute top-0 left-0 right-0 flex items-start justify-between p-4 sm:p-6">
        <div className="pointer-events-auto flex items-center gap-3 rounded-lg bg-[rgba(11,10,12,0.65)] backdrop-blur border border-[rgba(244,234,222,0.14)] px-3 py-2">
          <img src="/greinch-logo.png" alt="GREINCH" className="h-8 w-auto" />
          <div className="leading-none">
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#B9AFA6]">{spaceMode ? "GREINCH DEEP SPACE" : (underwaterMode ? "GREINCH DEEP SEA" : "GREINCH AIRSPACE")}</div>
            <div className="text-xs uppercase tracking-[0.24em] text-[#E94B4B]">{spaceMode ? "We deliver. Even in orbit." : (underwaterMode ? "We ship. Even to the abyss." : "You ask, we develop.")}</div>
          </div>
        </div>
        <div className="pointer-events-auto flex items-center gap-2">
          <button
            type="button"
            data-testid="world-skip-3d-link"
            onClick={() => {
              // Instant transition with a black fadeout overlay
              const ov = document.createElement("div");
              ov.style.cssText = "position:fixed;inset:0;background:#050307;z-index:9999;opacity:0;transition:opacity 180ms ease-out;pointer-events:none;";
              document.body.appendChild(ov);
              requestAnimationFrame(() => { ov.style.opacity = "1"; });
              setTimeout(() => { window.location.href = "/classic"; }, 200);
            }}
            className="text-[10px] sm:text-[11px] uppercase tracking-[0.16em] sm:tracking-[0.2em] px-2 sm:px-3 py-1.5 sm:py-2 rounded-md bg-[rgba(11,10,12,0.65)] backdrop-blur border border-[rgba(244,234,222,0.14)] hover:border-[#E94B4B]/60 hover:text-[#E94B4B] transition-colors"
          >
            <span className="sm:hidden">Skip 3D</span>
            <span className="hidden sm:inline">Skip 3D → Classic</span>
          </button>
        </div>
      </div>

      {/* Left telemetry */}
      <div className="absolute top-24 left-4 sm:left-6 space-y-2 pointer-events-none">
        <TelemetryCard
          label={spaceMode ? "ORBITAL ALT" : (underwaterMode ? "DEPTH" : "ALTITUDE")}
          value={spaceMode ? (500 + altitude).toFixed(0) : (underwaterMode ? Math.abs(altitude).toFixed(0) : altitude.toFixed(0))}
          unit={spaceMode ? "km" : "m"}
        />
        <TelemetryCard
          label={spaceMode ? "VELOCITY" : (underwaterMode ? "KNOTS" : "AIRSPEED")}
          value={spaceMode ? (speed * 42).toFixed(0) : speed.toFixed(0)}
          unit={spaceMode ? "km/h" : "kn"}
        />
        <TelemetryCard label="HEADING" value={(((-heading * 180) / Math.PI) % 360 + 360).toFixed(0).padStart(3, "0")} unit="°" />
        <TelemetryCard label={spaceMode ? "METEOR STRIKES" : (underwaterMode ? "SHARK BITES" : "BIRD STRIKES")} value={birdHits.toString().padStart(2, "0")} unit={birdHits > 0 ? "hits" : "clean"} accent={birdHits > 0 ? "#E94B4B" : undefined} />
        {birdHits > 0 && (
          <button
            type="button"
            data-testid="world-reset-birdhits-button"
            onClick={resetBirdHits}
            className="pointer-events-auto w-full px-3 py-1.5 rounded-md bg-[rgba(233,75,75,0.15)] backdrop-blur border border-[rgba(233,75,75,0.35)] text-[10px] uppercase tracking-[0.22em] text-[#EFE3D6] hover:bg-[rgba(233,75,75,0.28)] hover:border-[rgba(233,75,75,0.55)] transition-colors"
          >
            Reset strikes
          </button>
        )}
      </div>

      {/* Minimap (top-right) */}
      <div className="absolute top-24 right-4 sm:right-6 pointer-events-none">
        <Minimap position={position} heading={heading} />
      </div>

      {/* Waypoint arrow / prompt (bottom-center) - raised on mobile above d-pad */}
      <div className="absolute bottom-[220px] sm:bottom-16 left-1/2 -translate-x-1/2 pointer-events-none">
        {nearestId ? (
          nearestId === "earth-return" ? (
            <div
              data-testid="world-return-earth-prompt"
              className="inline-flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2 sm:py-3 rounded-md border-2 border-[#6BB6FF] bg-[rgba(11,20,40,0.85)] text-[#F4EADE] uppercase tracking-[0.18em] sm:tracking-[0.24em] text-[10px] sm:text-sm shadow-[0_0_40px_rgba(107,182,255,0.5)]"
            >
              <span className="h-2 w-2 rounded-full bg-[#6BB6FF] animate-pulse" />
              Fly Closer · Auto Return
            </div>
          ) : (
            <button
              type="button"
              data-testid="world-land-button"
              onClick={() => openPanel(nearestId)}
              className="pointer-events-auto inline-flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2 sm:py-3 rounded-md border-2 border-[#E94B4B] bg-[rgba(11,10,12,0.85)] text-[#F4EADE] uppercase tracking-[0.18em] sm:tracking-[0.24em] text-[10px] sm:text-sm hover:bg-[#6B1F47]/40 shadow-[0_0_40px_rgba(233,75,75,0.5)]"
            >
              <span className="h-2 w-2 rounded-full bg-[#E94B4B] animate-pulse" />
              {mobile ? "Tap to Land" : "[E] Land"} · {AIRPORTS.find((a) => a.id === nearestId)?.label}
            </button>
          )
        ) : waypoint ? (
          <div className="inline-flex items-center gap-2 sm:gap-4 px-3 sm:px-5 py-2 sm:py-3 rounded-md bg-[rgba(11,10,12,0.85)] backdrop-blur border-2 border-[#E94B4B] shadow-[0_0_35px_rgba(233,75,75,0.35)]">
            <svg width="22" height="22" viewBox="0 0 32 32" className="sm:w-8 sm:h-8" style={{ transform: `rotate(${waypoint.angle}rad)` }}>
              <path d="M 16 3 L 27 22 L 20 22 L 20 29 L 12 29 L 12 22 L 5 22 Z" fill="#E94B4B" stroke="#F4EADE" strokeWidth="1.5" />
            </svg>
            <div className="text-[9px] sm:text-[11px] uppercase tracking-[0.18em] sm:tracking-[0.24em]">
              <div className="text-[#B9AFA6]">Next · {waypoint.ap.label}</div>
              <div className="text-[#F4EADE] text-xs sm:text-sm">{waypoint.distance.toFixed(0)}m · {waypoint.ap.code}</div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Controls hint (bottom-right, desktop) */}
      {!mobile && (
        <div className="absolute bottom-6 right-6 pointer-events-none">
          <div className="px-3 py-2 rounded-md bg-[rgba(11,10,12,0.6)] backdrop-blur border border-[rgba(244,234,222,0.12)] text-[10px] uppercase tracking-[0.22em] text-[#B9AFA6]">
            <div><span className="text-[#F4EADE]">W/S</span> pitch · <span className="text-[#F4EADE]">A/D</span> {spaceMode ? "yaw" : "turn"}</div>
            <div><span className="text-[#F4EADE]">Shift</span> {spaceMode ? "thrust" : (underwaterMode ? "boost" : "boost / escape")} · <span className="text-[#F4EADE]">Space</span> {spaceMode ? "fire" : (underwaterMode ? "bubbles" : "drop")}</div>
            <div><span className="text-[#F4EADE]">E</span> {spaceMode ? "dock" : (underwaterMode ? "surface" : "land")}</div>
          </div>
        </div>
      )}

      {/* Escape charge indicator - shows when climbing high and boosting */}
      {!spaceMode && escapeCharge > 0.05 && transitionPhase === "idle" && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-16 pointer-events-none">
          <div className="px-4 py-3 rounded-md bg-[rgba(11,10,12,0.75)] backdrop-blur border border-[rgba(233,75,75,0.5)] text-center min-w-[220px]">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#E94B4B] mb-1">ATMOSPHERE ESCAPE</div>
            <div className="h-2 w-full rounded-full bg-[rgba(244,234,222,0.1)] overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#E94B4B] to-[#F0A020] transition-[width] duration-100"
                style={{ width: `${Math.min(100, escapeCharge * 100).toFixed(0)}%` }}
              />
            </div>
            <div className="text-[9px] uppercase tracking-[0.22em] text-[#B9AFA6] mt-1">Hold Shift to break orbit</div>
          </div>
        </div>
      )}

      {/* Transition overlay */}
      {transitionPhase === "ascending" && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(ellipse at center, rgba(255,255,255,0.85) 0%, rgba(180,180,255,0.5) 25%, rgba(11,8,30,0.9) 90%)",
            animation: "pulse 0.8s ease-out",
          }}
        />
      )}
      {transitionPhase === "descending" && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(ellipse at center, rgba(255,200,120,0.7) 0%, rgba(255,120,60,0.4) 30%, rgba(0,0,0,0.6) 90%)",
          }}
        />
      )}

      {/* Space mode indicator */}
      {spaceMode && transitionPhase !== "descending" && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 pointer-events-none">
          <div className="px-4 py-1.5 rounded-full bg-[rgba(20,10,40,0.75)] backdrop-blur border border-[rgba(140,90,255,0.5)] text-[10px] uppercase tracking-[0.32em] text-[#C5A8FF]">
            ⨁ Deep Space Sector · Land on planet to return
          </div>
        </div>
      )}

      {/* Underwater mode indicator + surface arrow */}
      {underwaterMode && (
        <>
          <div className="absolute top-4 left-1/2 -translate-x-1/2 pointer-events-none">
            <div className="px-4 py-1.5 rounded-full bg-[rgba(10,53,96,0.8)] backdrop-blur border border-[rgba(107,182,255,0.55)] text-[10px] uppercase tracking-[0.32em] text-[#EAF3FF]">
              ≋ Deep Sea Sector · Ascend to return to surface
            </div>
          </div>
          {/* Big glowing up-arrow indicator - smaller on mobile */}
          <div className="absolute top-1/2 right-4 sm:right-8 -translate-y-1/2 pointer-events-none flex flex-col items-center gap-1">
            <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.24em] sm:tracking-[0.32em] text-[#EAF3FF]">Surface</div>
            <svg width="36" height="52" viewBox="0 0 52 72" className="sm:w-[52px] sm:h-[72px] animate-pulse drop-shadow-[0_0_18px_rgba(107,182,255,0.9)]">
              <path d="M 26 4 L 46 34 L 34 34 L 34 68 L 18 68 L 18 34 L 6 34 Z" fill="#EAF3FF" stroke="#5A99CC" strokeWidth="2" />
            </svg>
            <div className="text-[8px] sm:text-[9px] uppercase tracking-[0.18em] sm:tracking-[0.22em] text-[#B9AFA6]">Depth {Math.abs(altitude).toFixed(0)}m</div>
          </div>
        </>
      )}

      {/* Earth/Airplane mode: dual world-transition indicators (up=space, down=submarine) */}
      {!underwaterMode && !spaceMode && (
        <div className="absolute top-1/2 right-2 sm:right-8 -translate-y-1/2 pointer-events-none flex flex-col items-center gap-2 sm:gap-5">
          {/* Up = Space */}
          <div className="flex flex-col items-center gap-0.5 sm:gap-1" data-testid="hud-arrow-space">
            <div className="text-[7px] sm:text-[10px] uppercase tracking-[0.18em] sm:tracking-[0.32em] text-[#C5A8FF]">Space</div>
            <svg width="20" height="28" viewBox="0 0 52 72" className="sm:w-[44px] sm:h-[60px] animate-pulse drop-shadow-[0_0_18px_rgba(197,168,255,0.85)]">
              <path d="M 26 4 L 46 34 L 34 34 L 34 68 L 18 68 L 18 34 L 6 34 Z" fill="#EFE3D6" stroke="#C5A8FF" strokeWidth="2" />
            </svg>
          </div>
          {/* Altitude readout in the middle */}
          <div className="text-[7px] sm:text-[10px] font-mono uppercase tracking-[0.2em] sm:tracking-[0.32em] text-[#B9AFA6] px-1.5 sm:px-2 py-0.5 sm:py-1 rounded border border-[rgba(185,175,166,0.35)] bg-[rgba(11,10,12,0.6)]">
            ALT {altitude.toFixed(0)}m
          </div>
          {/* Down = Submarine */}
          <div className="flex flex-col items-center gap-0.5 sm:gap-1" data-testid="hud-arrow-submarine">
            <svg width="20" height="28" viewBox="0 0 52 72" className="sm:w-[44px] sm:h-[60px] animate-pulse drop-shadow-[0_0_18px_rgba(107,182,255,0.85)]">
              <path d="M 26 68 L 6 38 L 18 38 L 18 4 L 34 4 L 34 38 L 46 38 Z" fill="#EAF3FF" stroke="#5A99CC" strokeWidth="2" />
            </svg>
            <div className="text-[7px] sm:text-[10px] uppercase tracking-[0.18em] sm:tracking-[0.32em] text-[#5A99CC]">Sub</div>
          </div>
        </div>
      )}

      {/* Space mode: down arrow back to earth */}
      {spaceMode && (
        <div className="absolute top-1/2 right-2 sm:right-8 -translate-y-1/2 pointer-events-none flex flex-col items-center gap-0.5 sm:gap-1" data-testid="hud-arrow-earth">
          <div className="text-[7px] sm:text-[10px] uppercase tracking-[0.18em] sm:tracking-[0.32em] text-[#8AC5FF]">Earth</div>
          <svg width="24" height="34" viewBox="0 0 52 72" className="sm:w-[52px] sm:h-[72px] animate-pulse drop-shadow-[0_0_18px_rgba(138,197,255,0.9)]">
            <path d="M 26 68 L 6 38 L 18 38 L 18 4 L 34 4 L 34 38 L 46 38 Z" fill="#EAF3FF" stroke="#8AC5FF" strokeWidth="2" />
          </svg>
        </div>
      )}

      {/* Mobile touch controls */}
      {mobile && (
        <>
          {/* Left pad - direction */}
          <div className="absolute bottom-4 left-4 grid grid-cols-3 gap-2 pointer-events-auto select-none">
            <div />
            <TouchBtn label="▲" {...holdBtn("up")} testid="touch-up" />
            <div />
            <TouchBtn label="◀" {...holdBtn("left")} testid="touch-left" />
            <TouchBtn label="●" muted testid="touch-center" />
            <TouchBtn label="▶" {...holdBtn("right")} testid="touch-right" />
            <div />
            <TouchBtn label="▼" {...holdBtn("down")} testid="touch-down" />
            <div />
          </div>
          {/* Right pad - boost */}
          <div className="absolute bottom-4 right-4 flex flex-col gap-2 pointer-events-auto">
            <TouchBtn label="BOOST" wide {...holdBtn("boost")} testid="touch-boost" />
          </div>
        </>
      )}
    </div>
  );
}

function TelemetryCard({ label, value, unit, accent }) {
  return (
    <div className="px-2 sm:px-3 py-1 sm:py-2 rounded-md bg-[rgba(11,10,12,0.6)] backdrop-blur border border-[rgba(244,234,222,0.12)] min-w-[72px] sm:min-w-[128px]">
      <div className="text-[8px] sm:text-[9px] uppercase tracking-[0.18em] sm:tracking-[0.24em] text-[#B9AFA6]">{label}</div>
      <div className="flex items-baseline gap-1 mt-0.5">
        <div className="text-base sm:text-2xl leading-none tracking-tight" style={{ color: accent || "#F4EADE" }}>{value}</div>
        <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.16em] sm:tracking-[0.2em] text-[#B9AFA6]">{unit}</div>
      </div>
    </div>
  );
}

function Minimap({ position, heading }) {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const upd = () => setIsMobile(mq.matches);
    upd();
    mq.addEventListener("change", upd);
    return () => mq.removeEventListener("change", upd);
  }, []);
  const SIZE = isMobile ? 92 : 150; // px
  const RANGE = 260; // world units mapped to the map
  const scale = SIZE / (RANGE * 2);
  const toXY = (wx, wz) => ({
    x: SIZE / 2 + (wx - position.x) * scale,
    y: SIZE / 2 + (wz - position.z) * scale,
  });
  return (
    <div
      className="relative rounded-lg bg-[rgba(11,10,12,0.65)] backdrop-blur border border-[rgba(244,234,222,0.14)] overflow-hidden"
      style={{ width: SIZE, height: SIZE }}
    >
      <div className="absolute top-1 left-2 text-[9px] uppercase tracking-[0.22em] text-[#B9AFA6]">Radar</div>
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(circle at center, rgba(233,75,75,0.10), transparent 60%), repeating-linear-gradient(0deg, rgba(244,234,222,0.06) 0 1px, transparent 1px 20px), repeating-linear-gradient(90deg, rgba(244,234,222,0.06) 0 1px, transparent 1px 20px)",
        }}
      />
      {/* Airports */}
      {AIRPORTS.map((ap) => {
        const p = toXY(ap.position[0], ap.position[2]);
        if (p.x < 0 || p.x > SIZE || p.y < 0 || p.y > SIZE) return null;
        return (
          <div
            key={ap.id}
            className="absolute h-2 w-2 rounded-full ring-2 ring-[rgba(11,10,12,0.6)]"
            style={{ left: p.x - 4, top: p.y - 4, background: ap.color }}
            title={ap.label}
          />
        );
      })}
      {/* Plane */}
      <div
        className="absolute"
        style={{
          left: SIZE / 2 - 6,
          top: SIZE / 2 - 6,
          transform: `rotate(${-heading}rad)`,
          transformOrigin: "6px 6px",
        }}
      >
        <div className="h-3 w-3 border-l-2 border-r-2 border-b-2 border-[#F4EADE] rounded-b-sm" />
      </div>
    </div>
  );
}

function TouchBtn({ label, wide, muted, testid, ...handlers }) {
  return (
    <button
      type="button"
      data-testid={testid}
      {...handlers}
      className={`select-none active:scale-95 transition-transform ${
        wide ? "h-16 w-24" : "h-14 w-14"
      } rounded-lg border ${
        muted
          ? "border-[rgba(244,234,222,0.08)] bg-transparent text-[#B9AFA6]"
          : "border-[rgba(244,234,222,0.2)] bg-[rgba(11,10,12,0.65)] backdrop-blur text-[#F4EADE] active:bg-[#6B1F47]/40"
      } text-sm font-bold`}
    >
      {label}
    </button>
  );
}
